import type { CrawlView } from "@shared/site-audit-report-view";
import { ReportSection } from "./section";

export function CrawlPanel({ crawl }: { crawl: CrawlView }) {
  if (crawl.kind !== "scored") {
    return (
      <ReportSection id="crawl" title="Crawl">
        <div className="rounded-[0.75rem] border border-border bg-card p-4">
          <p className="text-base font-medium">{crawl.heading}</p>
          {"detail" in crawl && crawl.detail ? (
            <p className="mt-2 text-sm leading-relaxed text-foreground/75">{crawl.detail}</p>
          ) : null}
        </div>
      </ReportSection>
    );
  }

  return (
    <ReportSection
      id="crawl"
      title="Crawl"
      hint={crawl.cached ? "This crawl was served from a recent saved result." : "Issue bars are relative to the largest count."}
    >
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Pages crawled" value={crawl.pagesLabel} />
        <Kpi label="On-page score" value={crawl.scoreLabel} />
        <Kpi label="Critical" value={String(crawl.criticalCount)} />
        <Kpi label="Warning" value={String(crawl.warningCount)} />
      </dl>
      {crawl.issues.length === 0 ? (
        <p className="text-sm text-foreground/75">No on-page issues were stored for this crawl.</p>
      ) : (
        <ul className="space-y-3">
          {crawl.issues.map((issue) => (
            <li key={issue.id} className="space-y-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="font-medium">{issue.label}</span>
                <span className={issue.tone === "problem" ? "text-destructive" : "text-foreground/75"}>
                  {issue.severityLabel} · {issue.countLabel}
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted" aria-hidden="true">
                <div
                  className={`h-2 rounded-full ${issue.tone === "problem" ? "bg-destructive" : "bg-foreground/40"}`}
                  style={{ width: `${Math.round(issue.bar * 100)}%` }}
                />
              </div>
              {issue.sampleUrls.length > 0 ? (
                <ul className="space-y-0.5">
                  {issue.sampleUrls.map((url) => (
                    <li key={url} className="truncate font-mono text-xs text-foreground/75">
                      {url}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {crawl.pagesWithIssues.length > 0 ? (
        <details className="rounded-[0.75rem] border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">Pages ({crawl.pagesWithIssues.length})</summary>
          <ul className="mt-3 space-y-3">
            {crawl.pagesWithIssues.map((page) => (
              <li key={page.url} className="text-sm">
                <p className="truncate font-mono text-xs">{page.url}</p>
                <p className="mt-1 text-foreground/75">{page.issues.join(", ")}</p>
                <p className="text-xs text-foreground/75">
                  {[page.statusCode != null ? `HTTP ${page.statusCode}` : null, page.loadTime, page.wordCount ? `${page.wordCount} words` : null]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </ReportSection>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[0.75rem] border border-border px-3 py-2">
      <dt className="text-xs text-foreground/75">{label}</dt>
      <dd className="mt-1 font-mono text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
